import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileSpreadsheet,
  Upload,
  CheckSquare,
  Square,
  AlertCircle,
  Loader2,
  X,
  BookOpen,
  Calendar,
  Users,
  Check,
  SkipForward,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { importReferences } from '@/lib/api';
import { parseCSV, parseExcel } from '@/features/sources/citations';
import type {
  ParsedImportEntry,
  ImportSession,
  ImportResult,
} from '@/features/sources/types';

interface DryRunEntry {
  index: number;
  title: string | null;
  status: 'new' | 'duplicate';
  reason?: string;
}

interface DryRunResult {
  entries: DryRunEntry[];
  new_count: number;
  duplicate_count: number;
}

const BATCH_SIZE = 100;
const RETRY_BATCH_SIZE = 25;
const MAX_RAW_LENGTH = 500;

interface SpreadsheetImporterProps {
  folderId: string | null;
  onCancel?: () => void;
  onFolderCreated?: (folderId: string) => void;
}

export function SpreadsheetImporter({
  folderId,
  onCancel,
  onFolderCreated,
}: SpreadsheetImporterProps) {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const [session, setSession] = useState<ImportSession | null>(null);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [isDryRunning, setIsDryRunning] = useState(false);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [createImportFolder, setCreateImportFolder] = useState(!folderId);

  // ---- File parsing ----

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0]!;
    const fileName = file.name;
    const ext = fileName.split('.').pop()?.toLowerCase();
    const isExcel = ext === 'xlsx' || ext === 'xls';

    setSession({
      format: 'csv',
      file_name: fileName,
      entries: [],
      total_count: 0,
      selected_count: 0,
      duplicate_count: 0,
      status: 'parsing',
      error: null,
    });

    try {
      let entries: ParsedImportEntry[];

      if (isExcel) {
        const buffer = await file.arrayBuffer();
        entries = parseExcel(buffer);
      } else {
        const content = await file.text();
        entries = parseCSV(content);
      }

      if (entries.length === 0) {
        setSession((prev) =>
          prev ? { ...prev, status: 'failed', error: 'No entries found. Check that the file has headers and data rows.' } : null
        );
        return;
      }

      // Detect which metadata fields were mapped
      const cols: string[] = [];
      const sample = entries[0]?.metadata;
      if (sample) {
        if (sample.title) cols.push('Title');
        if (sample.authors.length > 0) cols.push('Authors');
        if (sample.publication_year) cols.push('Year');
        if (sample.journal) cols.push('Journal');
        if (sample.doi) cols.push('DOI');
        if (sample.abstract) cols.push('Abstract');
        if (sample.keywords.length > 0) cols.push('Keywords');
      }
      setDetectedColumns(cols);

      setSession({
        format: 'csv',
        file_name: fileName,
        entries,
        total_count: entries.length,
        selected_count: entries.filter((e) => e.selected).length,
        duplicate_count: 0,
        status: 'ready',
        error: null,
      });

      // Auto dry-run for duplicate detection
      setIsDryRunning(true);
      try {
        const preparedEntries = entries.map((e) => ({
          metadata: e.metadata,
          raw: e.raw.length > 500 ? e.raw.slice(0, 500) + '...' : e.raw,
        }));

        const allDryRunEntries: DryRunEntry[] = [];
        const DRY_RUN_BATCH = 100;
        for (let i = 0; i < preparedEntries.length; i += DRY_RUN_BATCH) {
          const batch = preparedEntries.slice(i, i + DRY_RUN_BATCH);
          const data = await importReferences({
            entries: batch,
            folder_id: folderId || undefined,
            dry_run: true,
          }).catch((err) => { console.error('[SpreadsheetImporter] error:', err); return null; });
          if (data) {
            const batchResult = data as DryRunResult;
            for (const entry of batchResult.entries) {
              allDryRunEntries.push({ ...entry, index: entry.index + i });
            }
          }
        }

        const drResult: DryRunResult = {
          entries: allDryRunEntries,
          new_count: allDryRunEntries.filter((e) => e.status === 'new').length,
          duplicate_count: allDryRunEntries.filter((e) => e.status === 'duplicate').length,
        };
        setDryRunResult(drResult);

        if (drResult.duplicate_count > 0) {
          const dupIndices = new Set(
            drResult.entries.filter((e) => e.status === 'duplicate').map((e) => e.index)
          );
          const updatedEntries = entries.map((entry, idx) => ({
            ...entry,
            selected: !dupIndices.has(idx),
          }));
          setSession((prev) =>
            prev ? {
              ...prev,
              entries: updatedEntries,
              selected_count: updatedEntries.filter((e) => e.selected).length,
              duplicate_count: drResult.duplicate_count,
            } : null
          );
        }
      } catch {
        // Dry-run failure is non-blocking
      } finally {
        setIsDryRunning(false);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to parse file';
      setSession((prev) =>
        prev ? { ...prev, status: 'failed', error: message } : null
      );
    }
  }, [folderId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    disabled: session?.status === 'importing',
    multiple: false,
  });

  // ---- Selection ----

  function toggleEntry(id: string) {
    if (!session) return;
    const updated = session.entries.map((entry) =>
      entry.id === id ? { ...entry, selected: !entry.selected } : entry
    );
    setSession({
      ...session,
      entries: updated,
      selected_count: updated.filter((e) => e.selected).length,
    });
  }

  function toggleAll() {
    if (!session) return;
    const allSelected = session.entries.every((e) => e.selected);
    const updated = session.entries.map((e) => ({
      ...e,
      selected: !allSelected,
    }));
    setSession({
      ...session,
      entries: updated,
      selected_count: updated.filter((e) => e.selected).length,
    });
  }

  // ---- Import mutation ----

  const importMutation = useMutation({
    mutationFn: async (
      { entries, targetFolderId }: { entries: ParsedImportEntry[]; targetFolderId: string | null }
    ): Promise<ImportResult> => {
      if (!user) throw new Error('Not authenticated');

      const selected = entries.filter((e) => e.selected);
      const totalResult: ImportResult = {
        imported: 0,
        skipped_duplicates: 0,
        failed: 0,
        errors: [],
      };

      const prepareEntries = (batch: ParsedImportEntry[]) =>
        batch.map((e) => ({
          metadata: e.metadata,
          raw: e.raw.length > MAX_RAW_LENGTH ? e.raw.slice(0, MAX_RAW_LENGTH) + '...' : e.raw,
        }));

      const sendBatch = async (batch: ParsedImportEntry[]): Promise<ImportResult | null> => {
        return importReferences({
          entries: prepareEntries(batch),
          folder_id: targetFolderId || undefined,
        }).catch((err) => { console.error('[SpreadsheetImporter] error:', err); return null; }) as Promise<ImportResult | null>;
      };

      for (let i = 0; i < selected.length; i += BATCH_SIZE) {
        const batch = selected.slice(i, i + BATCH_SIZE);
        setImportProgress({ current: i, total: selected.length });

        const result = await sendBatch(batch);

        if (result) {
          totalResult.imported += result.imported;
          totalResult.skipped_duplicates += result.skipped_duplicates;
          totalResult.failed += result.failed;
          totalResult.errors.push(...result.errors);
        } else {
          for (let j = 0; j < batch.length; j += RETRY_BATCH_SIZE) {
            const retryBatch = batch.slice(j, j + RETRY_BATCH_SIZE);
            const retryResult = await sendBatch(retryBatch);

            if (retryResult) {
              totalResult.imported += retryResult.imported;
              totalResult.skipped_duplicates += retryResult.skipped_duplicates;
              totalResult.failed += retryResult.failed;
              totalResult.errors.push(...retryResult.errors);
            } else {
              totalResult.failed += retryBatch.length;
            }
          }
        }
      }

      return totalResult;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      setSession((prev) =>
        prev ? { ...prev, status: 'completed' } : null
      );
      setLastResult(result);

      if (result.imported > 0) {
        toast.success(
          `Imported ${result.imported} reference${result.imported > 1 ? 's' : ''}`
        );
      }
      if (result.skipped_duplicates > 0) {
        toast.info(
          `Skipped ${result.skipped_duplicates} duplicate${result.skipped_duplicates > 1 ? 's' : ''}`
        );
      }
      if (result.failed > 0) {
        toast.error(`Failed to import ${result.failed} reference(s)`);
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Import failed'
      );
      setSession((prev) =>
        prev ? { ...prev, status: 'failed', error: 'Import failed' } : null
      );
    },
  });

  async function handleImport() {
    if (!session || !user) return;
    setSession({ ...session, status: 'importing' });

    let targetFolderId = folderId;
    if (createImportFolder && !folderId) {
      try {
        const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        const folderName = `Import — ${session.file_name} — ${date}`;
        const { data: folder, error } = await supabase
          .from('folders')
          .insert({
            user_id: user.id,
            name: folderName,
            folder_type: 'import',
            description: null,
            parent_id: null,
            color: null,
          })
          .select()
          .single();

        if (!error && folder) {
          targetFolderId = folder.id;
          queryClient.invalidateQueries({ queryKey: ['folders'] });
          onFolderCreated?.(folder.id);
        }
      } catch {
        // Folder creation failure is non-blocking
      }
    }

    importMutation.mutate({ entries: session.entries, targetFolderId });
  }

  function handleReset() {
    setSession(null);
    setImportProgress(null);
    setLastResult(null);
    setDryRunResult(null);
    setDetectedColumns([]);
  }

  // ---- Render: drop zone ----

  if (!session) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="h-5 w-5" />
            Import CSV / Excel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer',
              isDragActive
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="h-8 w-8 text-slate-400" />
            <div>
              <p className="font-medium text-slate-700">
                {isDragActive
                  ? 'Drop your spreadsheet here'
                  : 'Drag & drop a .csv or .xlsx file here'}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                or click to browse
              </p>
              <p className="mt-2 text-xs text-slate-400">
                Columns are auto-detected (Title, Authors, Year, Journal, DOI, Abstract, Keywords...)
              </p>
            </div>
          </div>
          {onCancel && (
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ---- Render: error state ----

  if (session.status === 'failed') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-rose-600">
            <AlertCircle className="h-5 w-5" />
            Import Error
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">{session.error}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset}>
              Try Again
            </Button>
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---- Render: parsing state ----

  if (session.status === 'parsing') {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-3 py-12">
          <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
          <span className="text-sm text-slate-600">
            Parsing {session.file_name}...
          </span>
        </CardContent>
      </Card>
    );
  }

  // ---- Render: completed state ----

  if (session.status === 'completed' && lastResult) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Check className="h-5 w-5 text-emerald-600" />
            Import Complete
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            {lastResult.imported > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-emerald-600" />
                <span className="text-slate-700">
                  <span className="font-medium">{lastResult.imported}</span> imported
                </span>
              </div>
            )}
            {lastResult.skipped_duplicates > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <SkipForward className="h-4 w-4 text-amber-500" />
                <span className="text-slate-700">
                  <span className="font-medium">{lastResult.skipped_duplicates}</span> skipped (duplicates)
                </span>
              </div>
            )}
            {lastResult.failed > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                <span className="text-slate-700">
                  <span className="font-medium">{lastResult.failed}</span> failed
                </span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Import another file
            </Button>
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---- Render: entries list ----

  const allSelected = session.entries.every((e) => e.selected);
  const isImporting = session.status === 'importing';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="h-5 w-5" />
            {session.file_name}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleReset}
            disabled={isImporting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>{session.total_count} rows found</span>
          <Separator orientation="vertical" className="h-4" />
          <span>{session.selected_count} selected</span>
        </div>
        {detectedColumns.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            <span className="text-xs text-slate-400">Detected:</span>
            {detectedColumns.map((col) => (
              <Badge key={col} variant="outline" className="text-[10px] h-5">
                {col}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Select all toggle */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={toggleAll}
            disabled={isImporting}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-indigo-600 disabled:opacity-50"
          >
            {allSelected ? (
              <CheckSquare className="h-4 w-4 text-indigo-600" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
          <Badge variant="secondary">
            {session.selected_count} / {session.total_count}
          </Badge>
        </div>

        {/* Dry-run summary */}
        {isDryRunning && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking for duplicates...
          </div>
        )}
        {dryRunResult && !isDryRunning && dryRunResult.duplicate_count > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {dryRunResult.duplicate_count} duplicate{dryRunResult.duplicate_count > 1 ? 's' : ''} detected — deselected by default.
          </div>
        )}

        {/* Entries list */}
        <div className="h-[360px] overflow-y-auto rounded-md border">
          <div className="divide-y">
            {session.entries.map((entry, idx) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                disabled={isImporting}
                onToggle={() => toggleEntry(entry.id)}
                dupStatus={dryRunResult?.entries.find((e) => e.index === idx)}
              />
            ))}
          </div>
        </div>

        {/* Import progress */}
        {isImporting && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                Importing{importProgress ? ` batch ${Math.floor(importProgress.current / BATCH_SIZE) + 1}/${Math.ceil(importProgress.total / BATCH_SIZE)}` : ''}...
              </span>
              {importProgress && (
                <span className="font-medium">{Math.round((importProgress.current / importProgress.total) * 100)}%</span>
              )}
            </div>
            <Progress value={importProgress ? (importProgress.current / importProgress.total) * 100 : undefined} />
          </div>
        )}

        {/* Auto-create folder option */}
        {!folderId && (
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={createImportFolder}
              onChange={(e) => setCreateImportFolder(e.target.checked)}
              disabled={isImporting}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Create import folder automatically
          </label>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={isImporting}
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleImport}
            disabled={session.selected_count === 0 || isImporting}
          >
            {isImporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              `Import ${session.selected_count} reference${session.selected_count !== 1 ? 's' : ''}`
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =========================================
// Entry row sub-component
// =========================================

interface EntryRowProps {
  entry: ParsedImportEntry;
  disabled: boolean;
  onToggle: () => void;
  dupStatus?: { index: number; title: string | null; status: 'new' | 'duplicate'; reason?: string };
}

function EntryRow({ entry, disabled, onToggle, dupStatus }: EntryRowProps) {
  const { metadata } = entry;
  const isDuplicate = dupStatus?.status === 'duplicate';

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
        isDuplicate && !entry.selected && 'bg-amber-50/50',
        entry.selected ? 'bg-indigo-50/50' : 'hover:bg-slate-50',
        disabled && 'opacity-60 cursor-not-allowed'
      )}
    >
      <div className="mt-0.5 shrink-0">
        {entry.selected ? (
          <CheckSquare className="h-4 w-4 text-indigo-600" />
        ) : (
          <Square className="h-4 w-4 text-slate-400" />
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-900 line-clamp-2 flex-1">
            {metadata.title ?? 'Untitled'}
          </p>
          {dupStatus && (
            <Badge
              variant={isDuplicate ? 'secondary' : 'outline'}
              className={cn(
                'shrink-0 text-[10px]',
                isDuplicate ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200'
              )}
            >
              {isDuplicate ? 'Duplicate' : 'New'}
            </Badge>
          )}
        </div>

        {isDuplicate && dupStatus?.reason && (
          <p className="text-[11px] text-amber-600">{dupStatus.reason}</p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          {metadata.authors.length > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {metadata.authors.length <= 2
                ? metadata.authors.join(', ')
                : `${metadata.authors[0]} et al.`}
            </span>
          )}
          {metadata.publication_year && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {metadata.publication_year}
            </span>
          )}
          {metadata.journal && (
            <span className="flex items-center gap-1">
              <BookOpen className="h-3 w-3" />
              <span className="truncate max-w-[200px]">
                {metadata.journal}
              </span>
            </span>
          )}
        </div>

        {metadata.doi && (
          <p className="text-xs font-mono text-slate-400 truncate">
            DOI: {metadata.doi}
          </p>
        )}
      </div>
    </button>
  );
}
